// @ts-nocheck
import React from "react";
import { StageProgressCell, StageMetric } from "./StageProgressCell";
import { formatNumber, cn } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { Flame, Zap, Clock, Building2, Activity, Calendar } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";

import { toVnDateString } from "@apv-erp/status";
export interface ProcessGroupConfig {
  code: string;
  name: string;
  sortOrder: number;
}

export const DEFAULT_PROCESS_GROUPS: ProcessGroupConfig[] = [
  { code: 'PREPRESS', name: 'Chế Bản & Bình File', sortOrder: 1 },
  { code: 'PRINTING', name: 'In Offset', sortOrder: 2 },
  { code: 'LAMINATION', name: 'Cán Màng & Phủ UV', sortOrder: 3 },
  { code: 'DIE_CUTTING', name: 'Bế & Ép Kim', sortOrder: 4 },
  { code: 'FINISHING', name: 'Dán Hộp & Đóng Gói', sortOrder: 5 },
  { code: 'QC', name: 'KCS', sortOrder: 6 },
  { code: 'DELIVERY', name: 'Giao Hàng', sortOrder: 7 },
];

/** Số liệu công đoạn của 1 nhóm cột; nhóm tổng hợp lấy công đoạn nhánh đang có (Bế ← Ép kim / Dập nổi, Cán màng ← UV / Phủ) */
export function resolveStageMetric(stageMetrics: Record<string, StageMetric>, code: string): StageMetric | undefined {
  const own = stageMetrics?.[code];
  if (own && own.status !== 'NA') return own;
  const fallbacks: Record<string, string[]> = {
    DIE_CUTTING: ['FOIL_STAMPING', 'EMBOSSING'],
    LAMINATION: ['UV_COATING', 'COATING'],
  };
  for (const alt of fallbacks[code] || []) {
    if (stageMetrics?.[alt] && stageMetrics[alt].status !== 'NA') return stageMetrics[alt];
  }
  return own;
}

const STATUS_DISPLAY_NAMES: Record<string, string> = {
  DRAFT: "0. Bản nháp",
  PENDING_APPROVAL: "0. Chờ duyệt",
  PENDING: "0. Chờ duyệt",
  PLANNED: "1. Chờ SX",
  READY_FOR_SCHEDULING: "1. Chờ SX",
  MATERIALS_READY: "1. Chờ SX",
  RELEASED: "1. Chờ SX",
  IN_PROGRESS: "2. Đang SX",
  RUNNING: "2. Đang SX",
  MANUFACTURED: "3. Chờ KCS",
  COMPLETED: "4. Hoàn thành",
  CLOSED: "5. Đã đóng",
  CANCELLED: "Đã hủy",
  PAUSED: "Tạm dừng",
  ON_HOLD: "Tạm giữ",
};

export const formatKHSXTime = (startStr?: string | null, endStr?: string | null): string => {
  if (!startStr && !endStr) return "Chưa xếp lịch";
  const s = startStr ? new Date(startStr) : null;
  const e = endStr ? new Date(endStr) : null;
  if (s && e && !isNaN(s.getTime()) && !isNaN(e.getTime())) {
    const sFmt = `${s.getDate().toString().padStart(2, '0')}/${(s.getMonth() + 1).toString().padStart(2, '0')}`;
    const eFmt = `${e.getDate().toString().padStart(2, '0')}/${(e.getMonth() + 1).toString().padStart(2, '0')}`;
    return `${sFmt} → ${eFmt}`;
  }
  if (e && !isNaN(e.getTime())) {
    return `Đến ${e.getDate().toString().padStart(2, '0')}/${(e.getMonth() + 1).toString().padStart(2, '0')}`;
  }
  return "Chưa xếp lịch";
};

export type POGroupByOption = "none" | "customer" | "status" | "priority" | "day";

interface Props {
  orders: any[];
  activeProcessGroups: ProcessGroupConfig[];
  isLoading?: boolean;
  selectedIds?: Set<string>;
  onSelectRow?: (id: string) => void;
  onSelectAll?: () => void;
  isAllSelected?: boolean;
  groupBy?: POGroupByOption;
}

export function ProductionOrderMatrixGrid({
  orders,
  activeProcessGroups,
  isLoading,
  selectedIds,
  onSelectRow,
  onSelectAll,
  isAllSelected = false,
  groupBy = "none",
}: Props) {
  const navigate = useNavigate();

  const groupedOrders = React.useMemo(() => {
    if (!groupBy || groupBy === "none" || !orders || orders.length === 0) return null;

    const groups = new Map<
      string,
      {
        key: string;
        label: string;
        count: number;
        totalQty: number;
        completedQty: number;
        overdueCount: number;
        orders: any[];
      }
    >();

    orders.forEach((order) => {
      let key = "OTHER";
      let label = "Khác";

      if (groupBy === "customer") {
        const cust =
          order.customerName ||
          order.salesOrder?.customer?.shortName ||
          order.salesOrder?.customer?.name ||
          order.product?.customer?.name ||
          "KHÁCH HÀNG APV";
        key = cust;
        label = cust;
      } else if (groupBy === "status") {
        const st = order.status || "UNKNOWN";
        key = st;
        label = STATUS_DISPLAY_NAMES[st] || st;
      } else if (groupBy === "priority") {
        const prio = order.priority || "NORMAL";
        key = prio;
        label =
          prio === "URGENT"
            ? "HỎA TỐC"
            : prio === "HIGH"
            ? "ƯU TIÊN CAO"
            : prio === "LOW"
            ? "ƯU TIÊN THẤP"
            : "BÌNH THƯỜNG";
      } else if (groupBy === "day") {
        const rawDate = order.plannedStartDate || order.startDate || order.createdAt;
        if (rawDate) {
          const d = new Date(rawDate);
          if (!isNaN(d.getTime())) {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            key = `${yyyy}-${mm}-${dd}`;
            const todayStr = toVnDateString();
            const isToday = key === todayStr;
            label = `${isToday ? "Hôm nay — " : ""}${dd}/${mm}/${yyyy}`;
          } else {
            key = "NO_DATE";
            label = "Chưa có ngày kế hoạch";
          }
        } else {
          key = "NO_DATE";
          label = "Chưa có ngày kế hoạch";
        }
      }

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          label,
          count: 0,
          totalQty: 0,
          completedQty: 0,
          overdueCount: 0,
          orders: [],
        });
      }
      const g = groups.get(key)!;
      g.count += 1;
      const q = Number(order.qtyPlan || order.qtyOrder || 0);
      const c = Number(order.qtyProduced || order.completedQty || 0);
      g.totalQty += q;
      g.completedQty += c;
      if (
        order.deadline &&
        new Date(order.deadline) < new Date() &&
        order.status !== "COMPLETED" &&
        order.status !== "CLOSED"
      ) {
        g.overdueCount += 1;
      }
      g.orders.push(order);
    });

    return Array.from(groups.values());
  }, [orders, groupBy]);

  if (isLoading) {
    return (
      <div className="p-8 text-center text-muted-foreground animate-pulse">
        Đang tải dữ liệu Ma trận Tiến độ Lệnh Sản Xuất...
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="p-12 text-center text-muted-foreground border border-dashed rounded-xl bg-muted/20">
        Không có Lệnh Sản Xuất nào phù hợp với bộ lọc hiện tại.
      </div>
    );
  }

  const renderOrderRow = (order: any) => {
    const stageMetrics = order.processGroupMetrics || {};

    // Calculate overall completion percent for Qty progress bar
    const totalQty = order.qtyPlan || order.qtyOrder || 0;
    const completedQty =
      order.actualQuantity !== undefined
        ? order.actualQuantity
        : order.completedQty || 0;
    const overallPercent =
      order.progress !== undefined
        ? order.progress
        : totalQty > 0
        ? Math.min(100, Math.round((completedQty / totalQty) * 100))
        : 0;

    const isDone = [
      "COMPLETED",
      "DELIVERED",
      "INVOICED",
      "CLOSED",
      "CANCELLED",
    ].includes(order.status || "");

    // Days difference for deadline
    let diffDays: number | null = null;
    if (order.deadline) {
      const d = new Date(order.deadline);
      if (!isNaN(d.getTime())) {
        const now = new Date();
        diffDays = Math.ceil(
          (d.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)
        );
      }
    }

    const isOverdue = !isDone && diffDays !== null && diffDays < 0;
    const isNearDue =
      !isDone && diffDays !== null && diffDays >= 0 && diffDays <= 2;
    const isUrgentManual = !isDone && order.priority === "URGENT";
    const isCriticalUrgent = isUrgentManual || isOverdue;

    const customerName =
      order.customerName ||
      order.salesOrder?.customer?.shortName ||
      order.salesOrder?.customer?.name ||
      order.product?.customer?.name ||
      "KHÁCH HÀNG APV";

    const formattedDeadline = order.deadline
      ? new Date(order.deadline).toLocaleDateString("vi-VN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "--/--/----";

    const formattedCreatedDate = order.createdAt
      ? new Date(order.createdAt).toLocaleDateString("vi-VN")
      : "--/--/----";

    const displayStatusLabel =
      STATUS_DISPLAY_NAMES[order.status] || order.status;

    const isSelected = selectedIds?.has(order.orderId || order.id);

    return (
      <div
        key={order.id || order.orderId}
        onClick={() =>
          navigate({
            to: "/production-orders/$orderId",
            params: { orderId: order.orderId || order.id } as any,
          })
        }
        className={cn(
          "flex h-[64px] items-center table-row-hover transition-colors text-xs bg-white cursor-pointer group",
          isCriticalUrgent &&
            "apv-urgent-row border-l-4 !border-l-red-600 !bg-[#ffe4e6] hover:!bg-[#fecdd3]"
        )}
        data-testid="production-order-matrix-row"
        data-priority={order.priority}
      >
        {/* Sticky Col 1: Checkbox & LSX ID */}
        <div
          className={cn(
            "w-[152px] h-full px-3 sticky left-0 z-20 border-r border-slate-200 shrink-0 flex items-center gap-2 overflow-hidden",
            isCriticalUrgent
              ? "apv-urgent-sticky-col !bg-[#ffe4e6] group-hover:!bg-[#fecdd3]"
              : "bg-white group-hover:bg-slate-50"
          )}
        >
          {onSelectRow && (
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isSelected}
                onCheckedChange={() =>
                  onSelectRow(order.orderId || order.id)
                }
                aria-label={`Chọn ${order.orderId}`}
              />
            </div>
          )}
          <div className="flex flex-col justify-center gap-0.5 min-w-0">
            <div className="flex items-center gap-1">
              <span
                className={cn(
                  "text-xs font-mono whitespace-nowrap leading-tight",
                  isCriticalUrgent
                    ? "text-red-700 font-extrabold"
                    : "text-primary font-bold"
                )}
              >
                {order.lsxId || order.orderId}
              </span>
              {isCriticalUrgent && (
                <Flame
                  className="h-3.5 w-3.5 text-red-600 shrink-0 animate-pulse"
                  title="LSX Hỏa tốc / Quá hạn"
                />
              )}
              {!isCriticalUrgent && order.priority === "HIGH" && (
                <Zap
                  className="h-3.5 w-3.5 text-amber-500 shrink-0"
                  title="LSX Ưu tiên cao"
                />
              )}
            </div>
            <span className="text-[10px] text-slate-400 font-normal">
              {formattedCreatedDate}
            </span>
          </div>
        </div>

        {/* Sticky Col 2: Sản phẩm & Structure tích hợp Thumbnail */}
        <div
          className={cn(
            "w-[260px] h-full px-3 sticky left-[152px] z-20 border-r-2 border-slate-300 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.06)] shrink-0 flex items-center gap-2 overflow-hidden",
            isCriticalUrgent
              ? "apv-urgent-sticky-col !bg-[#ffe4e6] group-hover:!bg-[#fecdd3]"
              : "bg-white group-hover:bg-slate-50"
          )}
          title={order.productName || order.product?.name}
        >
          <ProductionThumbnailCell
            imageUrl={order.product?.image || order.productImage || order.productThumbnail}
            layoutUrl={order.printLayout?.fileUrl || order.printLayouts?.[0]?.fileUrl || order.layoutUrl}
            title={order.productName || order.product?.name || order.orderId}
            subtitle={order.lsxId || order.orderId}
            type="auto"
            size={34}
            className="shrink-0"
          />
          <div className="flex flex-col justify-center gap-0.5 min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className={cn(
                  "truncate text-xs",
                  isCriticalUrgent
                    ? "text-red-700 font-extrabold"
                    : "font-semibold text-slate-900"
                )}
              >
                {order.productName ||
                  order.product?.productName ||
                  order.product?.name ||
                  "Bao bì Offset (MTO)"}
              </span>
              {(order.componentsCount > 1 || order.components?.length > 1) && (
                <span className="shrink-0 text-[9px] font-bold px-1 py-0.2 rounded bg-sky-100 text-sky-700 border border-sky-200">
                  +{((order.componentsCount || order.components?.length) - 1)} SP
                </span>
              )}
            </div>
            <span className="text-[10px] text-slate-600 truncate">
              {order.dimensions ||
                order.techSpecs?.dimensions ||
                order.componentsSummary ||
                "24×11.5"}
            </span>
            <span
              className={cn(
                "text-[9px] uppercase truncate",
                isCriticalUrgent
                  ? "text-red-700 font-extrabold"
                  : "text-primary font-bold"
              )}
            >
              {customerName}
            </span>
          </div>
        </div>

        {/* Col 3: SL (pcs) & Progress */}
        <div className="w-[110px] h-full px-3 text-right font-label-code text-label-code shrink-0 flex flex-col justify-center gap-0.5">
          <span className="font-bold text-xs text-slate-900">
            {formatNumber(totalQty)}
          </span>
          <div className="flex flex-col items-end w-full">
            <div className="flex items-center justify-between w-full mb-0.5">
              <span className="text-[10px] text-slate-600 font-mono">
                {formatNumber(completedQty)}
              </span>
              <span className="text-[#2b8a3e] text-[10px] font-bold">
                {overallPercent}%
              </span>
            </div>
            <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#2b8a3e] transition-all duration-300"
                style={{ width: `${overallPercent}%` }}
              />
            </div>
          </div>
        </div>

        {/* Col 4: Hạn Giao & Thời Gian KHSX */}
        <div className="w-[145px] h-full px-2.5 border-r border-slate-200 shrink-0 flex flex-col justify-center gap-0.5">
          <div className="flex items-center gap-1">
            <Calendar className={cn("w-3 h-3 shrink-0", isCriticalUrgent ? "text-red-600" : "text-slate-400")} />
            <span className="text-[10px] text-slate-500 font-medium">Hạn PO:</span>
            <span className={cn("text-xs font-mono", isCriticalUrgent ? "text-red-700 font-extrabold" : "text-slate-800 font-bold")}>
              {formattedDeadline}
            </span>
          </div>
          <div className="flex items-center gap-1 text-[10px]">
            <Clock className="w-3 h-3 text-indigo-500 shrink-0" />
            <span className="text-slate-500">KHSX:</span>
            <span className="font-mono font-semibold text-indigo-700 truncate" title={formatKHSXTime(order.scheduleStartDate, order.scheduleEndDate)}>
              {formatKHSXTime(order.scheduleStartDate, order.scheduleEndDate)}
            </span>
          </div>
          {/* Cùng quy tắc nguy cơ trễ với danh sách đơn hàng (máy chủ tính theo ngày VN, bỏ công đoạn huỷ / đã xong) */}
          {order.isAtRisk && !isDone && (
            <span className="text-[9px] font-bold text-rose-600 bg-rose-50 px-1 py-0.2 rounded w-fit border border-rose-200">
              ⚠ KHSX trễ hạn giao
            </span>
          )}
        </div>

        {/* Col 5: Trạng thái & Mức ưu tiên */}
        <div className="w-[125px] h-full px-2.5 border-r border-slate-200 shrink-0 flex flex-col justify-center gap-1">
          <span className="font-bold text-xs text-slate-800 leading-tight">
            {displayStatusLabel}
          </span>
          {isOverdue ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-600 text-white border border-red-700 text-[10px] font-extrabold w-fit tracking-tight shadow-xs">
              <Clock className="w-2.5 h-2.5 text-white shrink-0" />
              TRỄ {Math.abs(diffDays!)} NGÀY
            </span>
          ) : isNearDue ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-bold w-fit tracking-tight">
              <Zap className="w-2.5 h-2.5 text-amber-600 shrink-0" />
              CẬN HẠN {diffDays === 0 ? "HÔM NAY" : `${diffDays} NGÀY`}
            </span>
          ) : isUrgentManual ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-600 text-white border border-red-700 text-[10px] font-extrabold w-fit tracking-tight shadow-xs">
              <Flame className="w-2.5 h-2.5 text-white shrink-0 animate-pulse" />
              HỎA TỐC
            </span>
          ) : order.priority === "HIGH" ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-bold w-fit tracking-tight">
              <Zap className="w-2.5 h-2.5 text-amber-600 shrink-0" />
              ƯU TIÊN CAO
            </span>
          ) : null}
        </div>

        {/* Cols 5+: Dynamic Process Groups */}
        {activeProcessGroups.map((group) => {
          const stageData = resolveStageMetric(stageMetrics, group.code);
          return (
            <div key={group.code} className="w-[130px] h-full shrink-0">
              <StageProgressCell stage={stageData} />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    // Desktop: không tự làm vùng cuộn — để vùng cuộn của trang danh sách cuộn cả 2 chiều, hàng tiêu đề sticky mới đứng yên khi cuộn
    <div className="w-full overflow-auto md:overflow-visible erp-table-scroll rounded-lg border border-slate-200 bg-white shadow-2xs relative">
      <div className="min-w-max">
        {/* Header Row - 84px height matching mockup */}
        <div className="flex border-b border-slate-200 h-[50px] items-center text-xs bg-[var(--apv-table-header-bg,#f8fafc)] sticky top-0 z-30 select-none whitespace-nowrap">
          {/* Sticky Col 1: Checkbox & LSX ID */}
          <div className="w-[152px] px-3 py-2 text-slate-600 sticky left-0 z-40 bg-[var(--apv-table-header-bg,#f8fafc)] border-r border-slate-200 shrink-0 font-semibold flex items-center gap-2 whitespace-nowrap">
            {onSelectAll && (
              <Checkbox
                checked={isAllSelected}
                onCheckedChange={onSelectAll}
                aria-label="Chọn tất cả"
                className="data-[state=checked]:bg-primary"
              />
            )}
            <span>LSX ID</span>
          </div>

          {/* Sticky Col 2: Sản phẩm / Quy cách */}
          <div className="w-[260px] px-3 py-2 text-slate-600 sticky left-[152px] z-40 bg-[var(--apv-table-header-bg,#f8fafc)] border-r-2 border-slate-300 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.06)] shrink-0 font-semibold flex items-center whitespace-nowrap">
            Sản phẩm / Quy cách
          </div>

          {/* Col 3: Tiến độ SL */}
          <div className="w-[110px] px-3 py-2 text-slate-600 text-right shrink-0 font-semibold flex items-center justify-end whitespace-nowrap">
            Tiến độ SL
          </div>

          {/* Col 4: Hạn Giao & KHSX */}
          <div className="w-[145px] px-3 py-2 text-slate-600 border-r border-slate-200 shrink-0 font-semibold flex items-center whitespace-nowrap">
            Hạn Giao &amp; KHSX
          </div>

          {/* Col 5: Trạng thái */}
          <div className="w-[125px] px-3 py-2 text-slate-600 border-r border-slate-200 shrink-0 font-semibold flex items-center whitespace-nowrap">
            Trạng thái
          </div>

          {/* Dynamic Process Group Headers (130px each) */}
          {activeProcessGroups.map((group) => (
            <div
              key={group.code}
              className="w-[130px] px-3 py-2 text-slate-600 shrink-0 font-semibold flex items-center border-r border-slate-200/30 text-xs whitespace-nowrap"
            >
              {group.name}
            </div>
          ))}
        </div>

        {/* Data Rows */}
        <div className="divide-y divide-slate-200">
          {groupedOrders ? (
            groupedOrders.map((grp) => (
              <React.Fragment key={`group-block-${grp.key}`}>
                {/* Group header bar spanning across table */}
                <div className="w-full bg-slate-100 border-y border-slate-300 py-1.5 px-3 select-none">
                  <div className="sticky left-3 flex items-center justify-between max-w-[750px]">
                    <div className="flex items-center gap-2">
                      {groupBy === "customer" && (
                        <Building2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                      )}
                      {groupBy === "status" && (
                        <Activity className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                      )}
                      {groupBy === "priority" && (
                        <Flame className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                      )}
                      {groupBy === "day" && (
                        <Calendar className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                      )}
                      <span className="font-bold text-xs text-slate-800 uppercase tracking-tight">
                        {grp.label}
                      </span>
                      <span className="text-[11px] px-1.5 py-0.2 rounded-full bg-white border border-slate-300 text-slate-700 font-semibold font-mono">
                        {grp.count} LSX
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-700 font-mono font-medium ml-4 flex items-center gap-3">
                      <span>Tổng SL: <strong>{formatNumber(grp.totalQty)}</strong> pcs</span>
                      <span>Hoàn thành: <strong className="text-emerald-700">{formatNumber(grp.completedQty)}</strong></span>
                      {grp.overdueCount > 0 && (
                        <span className="text-rose-600 font-bold bg-rose-50 border border-rose-200 px-1.5 py-0.2 rounded text-[10px]">
                          {grp.overdueCount} trễ hạn
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {/* Group orders */}
                {grp.orders.map(renderOrderRow)}
                {/* Dòng Subtotal tổng kết mỗi nhóm/ngày */}
                <div className="w-full bg-slate-50 border-b-2 border-slate-300 py-1.5 px-3 select-none text-xs">
                  <div className="sticky left-3 flex items-center justify-between max-w-[750px]">
                    <span className="font-bold text-slate-700">Tổng phụ {grp.label}:</span>
                    <div className="flex items-center gap-4 text-xs font-mono">
                      <span>Số LSX: <strong className="text-slate-900">{grp.count}</strong></span>
                      <span>Kế hoạch: <strong className="text-slate-900">{formatNumber(grp.totalQty)}</strong></span>
                      <span>Thực tế: <strong className="text-emerald-700">{formatNumber(grp.completedQty)}</strong> ({grp.totalQty > 0 ? Math.round((grp.completedQty / grp.totalQty) * 100) : 0}%)</span>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))
          ) : (
            orders.map(renderOrderRow)
          )}
        </div>
      </div>
    </div>
  );
}
